/**
 * Product Card Sun/Grade Toggle
 * Troca o card entre versão grau e sol via fetch + morph
 */
import { morph } from '@theme/morph';

/**
 * @typedef {{
 *   backgroundImageUrl: string | null,
 *   backgroundRotation: string | null,
 *   isCardGrau: boolean,
 *   isCardClassicos: boolean,
 * }} CardBackgroundSettings
 */

class ProductCardSunGradeToggle extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#abortController?.abort();
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = event => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-mode]');
    if (!(button instanceof HTMLButtonElement)) return;
    if (button.getAttribute('aria-pressed') === 'true') return;
    if (this.classList.contains('is-loading')) return;

    const mode = button.dataset.mode;
    if (mode !== 'grau' && mode !== 'sol') return;

    const url = mode === 'grau' ? this.dataset.grauUrl : this.dataset.solUrl;
    if (!url) return;

    event.preventDefault();
    event.stopPropagation();
    this.#swapCard(url);
  };

  /**
   * @param {string} productUrl
   */
  async #swapCard(productUrl) {
    const card = this.closest('zrzs-product-card-component');
    if (!(card instanceof HTMLElement)) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.classList.add('is-loading');
    card.classList.add('is-sun-grade-loading');

    // Mesmo padrão do morphCard: bg vem do data-* do card atual (settings do carousel/bloco)
    const backgroundSettings = this.#captureBackgroundSettings(card);
    const sunGradeSettings = this.#captureSunGradeSettings(card);

    try {
      const requestUrl = this.#buildRequestUrl(productUrl);
      const response = await fetch(requestUrl, {
        method: 'GET',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: this.#abortController.signal,
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newCard = doc.querySelector('zrzs-product-card-component');

      if (!(newCard instanceof HTMLElement)) {
        throw new Error('Card HTML not found');
      }

      // Garante que o HTML fetchado carregue os mesmos data-* de background / toggle
      this.#stampBackgroundAttributes(newCard, backgroundSettings);
      this.#stampSunGradeAttributes(newCard, sunGradeSettings);

      morph(card, newCard, { childrenOnly: false });
      this.#restoreBackgroundSettings(card, backgroundSettings);
      this.#stampSunGradeAttributes(card, sunGradeSettings);
      this.#refreshCardComponent(card);

      card
        .closest('li.product-grid__item')
        ?.setAttribute('data-matched-variants', '');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
    } finally {
      card.classList.remove('is-sun-grade-loading');
      const toggle = card.querySelector('product-card-sun-grade-toggle');
      toggle?.classList.remove('is-loading');
    }
  }

  /**
   * @param {HTMLElement} card
   * @returns {{
   *   enabled: boolean,
   *   trackBg: string | null,
   *   activeBg: string | null,
   *   activeText: string | null,
   *   idleText: string | null,
   * }}
   */
  #captureSunGradeSettings(card) {
    return {
      enabled: card.getAttribute('data-sun-grade-toggle') === 'true',
      trackBg: card.getAttribute('data-sg-track-bg'),
      activeBg: card.getAttribute('data-sg-active-bg'),
      activeText: card.getAttribute('data-sg-active-text'),
      idleText: card.getAttribute('data-sg-idle-text'),
    };
  }

  /**
   * @param {HTMLElement} card
   * @param {{
   *   enabled: boolean,
   *   trackBg: string | null,
   *   activeBg: string | null,
   *   activeText: string | null,
   *   idleText: string | null,
   * }} settings
   */
  #stampSunGradeAttributes(card, settings) {
    if (!settings.enabled) return;

    card.setAttribute('data-sun-grade-toggle', 'true');
    if (settings.trackBg)
      card.setAttribute('data-sg-track-bg', settings.trackBg);
    if (settings.activeBg)
      card.setAttribute('data-sg-active-bg', settings.activeBg);
    if (settings.activeText) {
      card.setAttribute('data-sg-active-text', settings.activeText);
    }
    if (settings.idleText)
      card.setAttribute('data-sg-idle-text', settings.idleText);
  }

  /**
   * @param {HTMLElement} card
   * @returns {CardBackgroundSettings}
   */
  #captureBackgroundSettings(card) {
    return {
      backgroundImageUrl: card.getAttribute('data-background-image-url'),
      backgroundRotation: card.getAttribute('data-background-rotation'),
      isCardGrau: card.classList.contains('zrzs-product-card-grau__container'),
      isCardClassicos: card.classList.contains(
        'zrzs-product-card-classicos__container'
      ),
    };
  }

  /**
   * @param {HTMLElement} card
   * @param {CardBackgroundSettings} settings
   */
  #stampBackgroundAttributes(card, settings) {
    if (settings.backgroundImageUrl) {
      card.setAttribute(
        'data-background-image-url',
        settings.backgroundImageUrl
      );
    }
    if (
      settings.backgroundRotation != null &&
      settings.backgroundRotation !== ''
    ) {
      card.setAttribute(
        'data-background-rotation',
        settings.backgroundRotation
      );
    }
  }

  /**
   * Reaplica o background após o morph (AJAX view não traz a imagem do bloco)
   * @param {HTMLElement} card
   * @param {CardBackgroundSettings} settings
   */
  #restoreBackgroundSettings(card, settings) {
    if (!settings.backgroundImageUrl) return;

    this.#stampBackgroundAttributes(card, settings);

    /** @type {{ applyCardGrauBackground?: Function, applyCardClassicosBackground?: Function }} */
    const cardWithBg = /** @type {any} */ (card);

    // Prefer methods do próprio card (já usados no morph de swatch)
    if (
      settings.isCardGrau &&
      typeof cardWithBg.applyCardGrauBackground === 'function'
    ) {
      cardWithBg.applyCardGrauBackground({
        backgroundImageUrl: settings.backgroundImageUrl,
        backgroundRotation: settings.backgroundRotation,
        hasBackground: true,
      });
      return;
    }

    if (
      settings.isCardClassicos &&
      typeof cardWithBg.applyCardClassicosBackground === 'function'
    ) {
      cardWithBg.applyCardClassicosBackground({
        backgroundImageUrl: settings.backgroundImageUrl,
        backgroundRotation: null,
        hasBackground: true,
      });
      return;
    }

    const backgroundContainer = card.querySelector(
      '.zrzs-product-card__background-container'
    );
    if (!(backgroundContainer instanceof HTMLElement)) return;

    if (settings.isCardGrau) {
      backgroundContainer.setAttribute(
        'style',
        `background-image: url(${settings.backgroundImageUrl}); background-size: cover; background-position: center; background-repeat: no-repeat; background-color: transparent; transform: rotate(${settings.backgroundRotation || 0}deg);`
      );
    } else if (settings.isCardClassicos) {
      backgroundContainer.setAttribute(
        'style',
        `background-image: url(${settings.backgroundImageUrl}); background-size: 98%; background-position: center; background-repeat: no-repeat; background-color: transparent;`
      );
    } else {
      return;
    }

    backgroundContainer.classList.add(
      'zrzs-product-card__background-container--has-image'
    );
  }

  /**
   * @param {string} productUrl
   * @returns {string}
   */
  #buildRequestUrl(productUrl) {
    const url = new URL(productUrl, window.location.origin);
    const view = this.dataset.cardView || 'card';
    url.searchParams.set('view', view);

    url.searchParams.set('sun_grade_toggle', '1');
    this.#appendColorParam(url, 'sg_track', this.dataset.sgTrackBg);
    this.#appendColorParam(url, 'sg_active_bg', this.dataset.sgActiveBg);
    this.#appendColorParam(url, 'sg_active_text', this.dataset.sgActiveText);
    this.#appendColorParam(url, 'sg_idle_text', this.dataset.sgIdleText);

    // Preserva o nível do heading do título do card (h2 na PLP) ao trocar grau/sol
    const titleTag = this.closest('zrzs-product-card-component')?.getAttribute(
      'data-title-tag'
    );
    if (titleTag) url.searchParams.set('title_tag', titleTag);

    return url.toString();
  }

  /**
   * @param {URL} url
   * @param {string} key
   * @param {string | undefined} value
   */
  #appendColorParam(url, key, value) {
    if (!value) return;
    url.searchParams.set(key, value.replace(/^#/, ''));
  }

  /**
   * Rebind listeners after morph (host element is preserved)
   * @param {HTMLElement} card
   */
  #refreshCardComponent(card) {
    // @ts-ignore - methods exist on ZRZSProductCardComponent
    if (typeof card.removeEventListeners === 'function') {
      // @ts-ignore
      card.removeEventListeners();
    }
    // @ts-ignore
    if (typeof card.setupElements === 'function') {
      // @ts-ignore
      card.setupElements();
    }
    // @ts-ignore
    if (typeof card.attachEventListeners === 'function') {
      // @ts-ignore
      card.attachEventListeners();
    }
    // @ts-ignore
    if (typeof card.initializeSwatches === 'function') {
      // @ts-ignore
      card.initializeSwatches();
    }
    // @ts-ignore
    if (typeof card.setupSwatchScroller === 'function') {
      // @ts-ignore
      card.setupSwatchScroller();
    }
    // @ts-ignore
    if (card.prefetchedCards instanceof Map) {
      // @ts-ignore
      card.prefetchedCards.clear();
      // @ts-ignore
      card.prefetchedImages?.clear?.();
      // @ts-ignore
      card.prefetching?.clear?.();
    }
  }
}

if (!customElements.get('product-card-sun-grade-toggle')) {
  customElements.define(
    'product-card-sun-grade-toggle',
    ProductCardSunGradeToggle
  );
}
